import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { MappingItemsSelectionNode } from '../../../../interfaces/mapping-items-selection-node.interface';

@UntilDestroy()
@Component({
    selector: 'mapping-items-tree-node',
    templateUrl: 'mapping-items-selection-tree-node.component.html',
    styleUrls: ['mapping-items-selection-tree-node.component.scss'],
})
export class MappingItemsSelectionTreeNodeComponent {
    @Input() node: MappingItemsSelectionNode;
    @Input() depth: number = 0;
    @Input() disableDeselection = false; // Deselection is disabled for nodes that their parents are already selected
    @Input() language: string | undefined;
    @Output() toggleSelect = new EventEmitter<MappingItemsSelectionNode>();

    toggleExpand() {
        this.node.expanded = !this.node.expanded;
    }

    onToggleSelect(): void {
        if (this.disableDeselection) {
            return;
        }
        this.toggleSelect.emit(this.node);
    }

    /** The name shown in the row; grouped accounts without their account number (a group id). */
    get label(): string {
        const data: any = this.node?.data;
        if (data == null) return '';
        if (data.groupId) return data.name ?? '';
        const name = data.title || data.caption || data.name || '';
        return data.accountNumber ? `${data.accountNumber} ${name}` : name;
    }

    /**
     * REV-20899: render the "final" displayed values — imported balance plus
     * the aggregated transactional reclass that lives on the node's data
     * (stamped per-account by `applyReclassificationsToFinStatements` and
     * aggregated up to FLI / SubFLI / group / section by
     * `GroupingHelper.recalculateGroupSummaries`).
     */
    get currentValue(): number | undefined {
        const data: any = this.node?.data;
        if (data == null) return undefined;
        const balance = data.calculatedClosingBalance;
        const reclass = data.closingReClass;
        if (balance == null && (reclass == null || reclass === 0)) return balance ?? undefined;
        return (balance ?? 0) + (reclass ?? 0);
    }

    get previousValue(): number | undefined {
        const data: any = this.node?.data;
        if (data == null) return undefined;
        const balance = data.calculatedOpeningBalance;
        const reclass = data.openingReClass;
        if (balance == null && (reclass == null || reclass === 0)) return balance ?? undefined;
        return (balance ?? 0) + (reclass ?? 0);
    }
}
